// Auto-shorten a component/product name for compact display (heatmap
// column headers, tight chart axes, etc). Vendor-agnostic.
//
// Two rules applied in order:
//   1. Strip a vendor-name prefix from the front. The set of prefixes we
//      try includes the canonical vendor name *and* any alias forms
//      (HPE ↔ Hewlett Packard Enterprise, IBM ↔ International Business
//      Machines, etc.). Each form is also "peeled" of trailing corporate
//      suffixes (Inc, Corp, Networks, Holdings, …) so
//      "Palo Alto Firewall" matches when the vendor field says
//      "Palo Alto Networks".
//   2. Strip parenthetical descriptions: "Cortex XDR (Extended Detection
//      & Response)" → "Cortex XDR".
//
// If a column needs a label that auto-rules can't produce, the source
// table should grow a display_name override and we layer it on top of
// this helper.

const CORPORATE_SUFFIXES = [
  'networks', 'network',
  'inc', 'inc.', 'incorporated',
  'corp', 'corp.', 'corporation',
  'llc', 'ltd', 'limited',
  'software', 'technologies', 'technology', 'tech',
  'systems', 'system',
  'solutions', 'group',
  'co', 'co.', 'company',
  // public-company / international forms — added to cover Holdings-style
  // names (CrowdStrike Holdings, Palantir Technologies) and Dutch /
  // global incorporations (Elastic N.V., Heineken N.V.).
  'holdings', 'industries', 'enterprise', 'enterprises',
  'international', 'intl', 'global', 'worldwide',
  'nv', 'plc', 'sa', 'ag', 'gmbh',
];

// Built-in aliases for vendors that show up under multiple legal names
// in our data. Each entry is a list of equivalent names; the helper
// resolves by membership, so the vendor field can be stored in any of
// the listed forms. Add to this list when you hit a real mismatch in
// the wild — keep entries surgical.
const VENDOR_ALIAS_GROUPS = [
  ['HPE', 'Hewlett Packard Enterprise', 'Hewlett-Packard'],
  ['IBM', 'International Business Machines'],
  ['AWS', 'Amazon Web Services', 'Amazon'],
  ['GCP', 'Google Cloud Platform', 'Google Cloud', 'Google'],
  ['Palo Alto Networks', 'Palo Alto'],
  ['Cisco', 'Cisco Systems'],
];

function normalizeKey(s) {
  return s ? s.trim().toLowerCase() : '';
}

function resolveAliasesFor(vendor) {
  const key = normalizeKey(vendor);
  if (!key) return [];
  for (const group of VENDOR_ALIAS_GROUPS) {
    if (group.some(name => normalizeKey(name) === key)) return group;
  }
  return [];
}

// Peel trailing corporate suffixes one at a time:
//   "Palo Alto Networks"    → ["Palo Alto Networks", "Palo Alto"]
//   "Cisco Systems Inc"     → ["Cisco Systems Inc", "Cisco Systems", "Cisco"]
//   "Microsoft Corporation" → ["Microsoft Corporation", "Microsoft"]
//   "Microsoft"             → ["Microsoft"]
function peelCorporateSuffixes(vendor) {
  if (!vendor) return [];
  const trimmed = vendor.trim();
  if (!trimmed) return [];
  const words = trimmed.split(/\s+/);
  const out = [trimmed];
  let i = words.length;
  while (i > 1) {
    const tail = words[i - 1].toLowerCase().replace(/[.,]/g, '');
    if (!CORPORATE_SUFFIXES.includes(tail)) break;
    i -= 1;
    const variant = words.slice(0, i).join(' ');
    if (variant) out.push(variant);
  }
  return out;
}

// Compose the full prefix-variant list from the vendor + any aliases
// (explicit or auto-resolved from VENDOR_ALIASES), each expanded via
// suffix peeling, deduped, and sorted longest-first.
function buildPrefixVariants(vendor, aliases) {
  const seeds = new Set();
  if (vendor) seeds.add(vendor.trim());

  const resolvedAliases = Array.isArray(aliases) && aliases.length
    ? aliases
    : resolveAliasesFor(vendor);
  for (const a of resolvedAliases) {
    if (a) seeds.add(String(a).trim());
  }

  const variants = new Set();
  for (const seed of seeds) {
    for (const v of peelCorporateSuffixes(seed)) variants.add(v);
  }
  return [...variants].filter(Boolean).sort((a, b) => b.length - a.length);
}

function stripVendorPrefix(name, variants) {
  const lowerName = name.toLowerCase();
  for (const v of variants) {
    const lv = v.toLowerCase();
    if (lowerName === lv) return name;
    // Match prefix followed by a word boundary (space, apostrophe, hyphen)
    if (lowerName.startsWith(lv + ' ')) return name.slice(v.length + 1);
    if (lowerName.startsWith(lv + "'s ")) return name.slice(v.length + 3);
    if (lowerName.startsWith(lv + '-')) return name.slice(v.length + 1);
  }
  return name;
}

function stripParentheticals(name) {
  return name.replace(/\s*\([^)]*\)\s*/g, ' ').replace(/\s{2,}/g, ' ').trim();
}

/**
 * @param {string} name      Component/product name to shorten.
 * @param {string} vendor    Canonical vendor name (used for prefix strip).
 * @param {string[]} [aliases]  Optional alternate vendor-name forms to
 *   also try as prefixes (e.g., ["HPE", "Hewlett-Packard"]). If omitted,
 *   the helper auto-looks-up aliases in the built-in VENDOR_ALIASES map.
 */
export function componentLabel(name, vendor, aliases) {
  if (!name) return name;
  const variants = buildPrefixVariants(vendor, aliases);
  let out = stripVendorPrefix(name.trim(), variants);
  out = stripParentheticals(out);
  return out.trim() || name;
}

export default componentLabel;
